// filepath: apps/web/src/features/admin/templates/export-import/useTemplateBundleExport.ts
//
// FR-UI-ADM-13 AC-1 — TanStack Query hook for the template
// bundle export verb (GET /api/templates/{templateID}/versions/{version}/bundle).
//
// INV-1 (cross-tenant): the cache key includes `tenantSlug` as
// the 2nd segment. Mirrors `templateRegistryKeys.registry`
// (`["admin", tenantSlug, "template-registry"]`) and
// `templateUpdateKeys.list`
// (`["admin", tenantSlug, "template-updates"]`).
//
// FR-NFR-3 / ARCH Rule 3 (idempotency): the GET verb is
// naturally idempotent. The hook sends a defensive
// `X-Idempotency-Key: crypto.randomUUID()` header so the wire
// shape is symmetric with the install + apply-update verbs in
// case the backend ever promotes the export verb to a POST (e.g.
// for server-side audit logging).
//
// FR-UI-CORE-10 (5-state fetch contract): the hook carries the
// typed union `TemplateBundleExportFetchError` on failure. The
// `ExportButton` maps each kind to its dedicated card.
//
// SCOPE NOTE: the mutation returns a `Blob` (the raw bundle
// bytes) on success. The button layer is responsible for
// converting the blob into a browser download via
// `URL.createObjectURL` + `<a download="<id>-<version>.ascoa-template">`.
// The hook itself does not trigger the download — the hook is
// testable in isolation (the test harness asserts the blob is
// returned, not the download side-effect).

import { useMutation } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type { TemplateBundleExportFetchError } from "./types";

/**
 * Cache keys for the template bundle export verb. tenantSlug
 * is the 2nd segment (INV-1). Mirrors the existing registry /
 * update key shapes under `apps/web/src/features/admin/templates/`.
 */
export const templateBundleExportKeys = {
  export: (tenantSlug: string, templateId: string, version: string) =>
    [
      "admin",
      tenantSlug,
      "template-bundle-export",
      templateId,
      version,
    ] as const,
};

/**
 * useTemplateBundleExport — GET
 * /api/templates/{templateID}/versions/{version}/bundle.
 *
 * Returns the `Blob` (raw bundle bytes, `application/zip`) on
 * success. The button layer wraps the blob in a synthetic anchor
 * click to trigger the browser download.
 *
 * The mutation is enabled for both staging and production
 * tenants (the export endpoint is read-only and JWT-gated).
 * The mutation carries `X-Idempotency-Key` defensively (the GET
 * is naturally idempotent; the header is future-proofing).
 */
export function useTemplateBundleExport() {
  const { tenantSlug } = useTenant();
  // Reference the cache-key helper with tenantSlug so future
  // iterations that introduce cache invalidation (e.g.
  // server-side audit logging that surfaces in the registry)
  // have the variable in closure scope. The cache-key shape is
  // asserted by the unit test under
  // useTemplateBundleExport.test.tsx (AC-7 INV-1).
  void templateBundleExportKeys.export(tenantSlug, "x", "x");
  return useMutation<
    Blob,
    Error | TemplateBundleExportFetchError,
    {
      templateID: string;
      version: string;
    }
  >({
    mutationFn: async ({ templateID, version }) => {
      // ISS-377 round 2.1 (R2-B2): migrated to apiFetch. The
      // `Accept: application/zip` header is forwarded via
      // `init.headers` so the server returns the binary zip
      // body. The defensive `X-Idempotency-Key` (this verb is
      // GET, naturally idempotent, but the wire shape is
      // symmetric with the install + apply-update verbs) is
      // forwarded via `init.headers` directly — apiFetch's
      // `idempotencyKey` flag SKIPS attachment for GET
      // (`attachIdempotencyKey` is a no-op for reads; the
      // server's idempotency middleware only kicks in for
      // state-changing methods).
      const r = await apiFetch(
        `/api/templates/${encodeURIComponent(templateID)}/versions/${encodeURIComponent(version)}/bundle`,
        {
          method: "GET",
          init: {
            headers: {
              // Explicit Accept so the server returns the binary
              // zip body (the default would be JSON). Mirrors the
              // content-type the server sends on success.
              Accept: "application/zip,application/octet-stream;q=0.9",
              // Defensive X-Idempotency-Key (the verb is GET —
              // naturally idempotent; the header is for wire-
              // shape symmetry with the install/apply-update
              // verbs).
              "X-Idempotency-Key": crypto.randomUUID(),
            },
          },
        },
      );
      if (r.status === 403) {
        const body = (await r.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        const err: TemplateBundleExportFetchError = {
          kind: "permission_denied",
          message: body.error ?? "permission denied",
          retriable: false,
        };
        throw err;
      }
      if (r.status === 404) {
        const body = (await r.json().catch(() => ({}))) as {
          error?: string;
        };
        const err: TemplateBundleExportFetchError = {
          kind: "template_not_found",
          message: body.error ?? "template or version not found",
          retriable: false,
        };
        throw err;
      }
      if (r.status === 413) {
        const body = (await r.json().catch(() => ({}))) as {
          error?: string;
        };
        const err: TemplateBundleExportFetchError = {
          kind: "bundle_too_large",
          message: body.error ?? "bundle too large",
          retriable: false,
        };
        throw err;
      }
      if (r.status === 422) {
        // Defined for symmetry with the future import path; the
        // export verb does not normally produce this kind (the
        // bundle is already validated on publish).
        const body = (await r.json().catch(() => ({}))) as {
          error?: string;
          errors?: Array<{ line: string }>;
        };
        const err: TemplateBundleExportFetchError = {
          kind: "validation_failed",
          message: body.error ?? "bundle validation failed",
          retriable: false,
          errors: body.errors,
        };
        throw err;
      }
      if (r.status === 429) {
        const body = (await r.json().catch(() => ({}))) as {
          error?: string;
        };
        const err: TemplateBundleExportFetchError = {
          kind: "rate_limit",
          message: body.error ?? "rate limited",
          retriable: true,
        };
        throw err;
      }
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return await r.blob();
    },
    // No onSuccess cache invalidation — the export verb is
    // read-only and the registry row it serializes from is
    // already in the cache (the table reads `row` directly).
    // The button layer triggers the browser download in its own
    // onSuccess handler (see ExportButton.tsx).
  });
}

/**
 * classifyTemplateBundleExportError — maps a transport-level
 * ApiError to the typed `TemplateBundleExportFetchError` union.
 * Used by the button layer when the mutation throws an
 * `ApiError` (i.e. a status that is NOT mapped by the in-mutation
 * switch — a default 5xx or a network failure).
 */
export function classifyTemplateBundleExportError(
  err: ApiError,
): TemplateBundleExportFetchError {
  switch (err.status) {
    case 403:
      return {
        kind: "permission_denied",
        message: err.message || "permission denied",
        retriable: false,
      };
    case 404:
      return {
        kind: "template_not_found",
        message: err.message || "template or version not found",
        retriable: false,
      };
    case 413:
      return {
        kind: "bundle_too_large",
        message: err.message || "bundle too large",
        retriable: false,
      };
    case 429:
      return {
        kind: "rate_limit",
        message: err.message || "rate limited",
        retriable: true,
      };
    case 0:
      return {
        kind: "fetch_failure",
        message: err.message || "network error",
        retriable: true,
      };
    default:
      return {
        kind: "fetch_failure",
        message: err.message || err.code || "fetch failure",
        retriable: err.status >= 500,
      };
  }
}
